/**
 * Hierarchical query key factory for the Employees feature.
 *
 * @module features/employees/queryKeys
 *
 * ## Key hierarchy
 * ```
 * ["employees"]                                      ← invalidates everything employee-related
 * ["employees", "list", EmployeeFilters]             ← Employee[] (typeahead + non-paginated callers)
 * ["employees", "paginated-list", EmployeeFilters]   ← PaginatedResponse<Employee> (useEmployees)
 * ["employees", "detail", id]                       ← invalidates a single employee
 * ["employees", "context", id]                      ← employee context anchored to today
 * ["employees", "context", id, EmployeeContextParams] ← context re-anchored / previewed (LOA-1323)
 * ```
 *
 * `context(id)` is a prefix of every parameterised context key, so
 * invalidating it also drops any preview or as_of-anchored variants.
 *
 * NOTE: `list` and `paginatedList` are intentionally separate keys.
 * `list` callers cache `Employee[]`; `paginatedList` caches the full
 * `PaginatedResponse<Employee>` envelope (count + results). Sharing a key
 * would cause React Query cache collisions where one consumer reads the
 * wrong shape from the cache.
 */

import type { EmployeeContextParams, EmployeeFilters } from "./types";

export const employeeKeys = {
  all: () => ["employees"] as const,
  list: (filters: EmployeeFilters) => ["employees", "list", filters] as const,
  paginatedList: (filters: EmployeeFilters) => ["employees", "paginated-list", filters] as const,
  detail: (id: string) => ["employees", "detail", id] as const,
  context: (id: string, params?: EmployeeContextParams) =>
    params ? (["employees", "context", id, params] as const) : (["employees", "context", id] as const),
};
